/** API client for the project-level EBM capability recommendation workflow. */

import { AsyncJobStartResponse } from '../types/asyncJob';
import { ApplicationMatch, CapabilityPath } from '../types/capabilityMapping';
import {
  CapabilityDefinitionResponse,
  ConfirmedCapabilityRecommendationResponse,
  CapabilityRecommendationResponse,
  ConfirmCapabilityRecommendationResponse,
} from '../types/capabilityRecommendation';

import { apiClient } from './apiClient';
import { ApiError } from './apiClient';
import { startAndPollJob } from './jobPollingService';

export class CapabilityRecommendationService {
  /** Generate one EBM capability recommendation from complete project context. */
  public async generate(
    projectId: number,
  ): Promise<CapabilityRecommendationResponse> {
    return startAndPollJob<CapabilityRecommendationResponse>(
      projectId,
      () => apiClient.post<AsyncJobStartResponse>(
        `/api/project/${projectId}/context/capability_recommendation/generate/async`,
      ),
    );
  }

  /** Confirm or catalogue-backed override the capability for downstream agents. */
  public async confirm(
    projectId: number,
    capabilityPath: CapabilityPath,
    rationale?: string,
    selectedApplications: ApplicationMatch[] = [],
  ): Promise<ConfirmCapabilityRecommendationResponse> {
    return apiClient.post<ConfirmCapabilityRecommendationResponse>(
      `/api/project/${projectId}/context/capability_recommendation/confirm`,
      {
        capability_path: capabilityPath,
        rationale: rationale ?? undefined,
        selected_applications: selectedApplications,
      },
    );
  }

  /** Get an official EBM definition without confirming a selection. */
  public async getDefinition(
    projectId: number,
    capabilityPath: CapabilityPath,
  ): Promise<CapabilityDefinitionResponse> {
    return apiClient.post<CapabilityDefinitionResponse>(
      `/api/project/${projectId}/context/capability_recommendation/definition`,
      { capability_path: capabilityPath },
    );
  }

  /** Load the latest architect-confirmed capability from durable context. */
  public async getConfirmed(
    projectId: number,
  ): Promise<ConfirmedCapabilityRecommendationResponse | null> {
    try {
      return await apiClient.get<ConfirmedCapabilityRecommendationResponse>(
        `/api/project/${projectId}/context/capability_recommendation/confirmed`,
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /** Query catalogue applications directly mapped to a capability path. */
  public async getApplications(
    projectId: number,
    capabilityPath: CapabilityPath,
  ): Promise<ApplicationMatch[]> {
    return apiClient.post<ApplicationMatch[]>(
      `/api/project/${projectId}/context/capability_recommendation/applications`,
      capabilityPath,
    );
  }
}

export const capabilityRecommendationService = new CapabilityRecommendationService();